import { Component } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import cn from 'classnames';

import { evNavigationClick, evTopLevelNavigationClick } from 'events/headerFooter';
import { SESSIONSTORAGE_KEY_REWARDS_INFO } from 'constants/rewardsInfo';

import PrimeLogo from 'components/icons/vipDashboard/PrimeLogo';
import VipOnlyLogo from 'components/icons/vipDashboard/VipOnlyLogo';
import Link from 'components/hf/HFLink';
import { trackEvent, trackLegacyEvent } from 'helpers/analytics';
import { onEvent } from 'helpers/EventHelpers';
import { track } from 'apis/amethyst';
import css from 'styles/components/hf/zappos/accountDropdowns.scss';
import { removeFromSessionStorage } from 'helpers/ClientUtils';
import { toThousandsSeparator, toUSD } from 'helpers/NumberFormats';

class AccountDropdown extends Component {
  constructor(props) {
    super(props);
    this.onResize = debounce(this.onResize.bind(this), 100);
  }

  state = {
    showOutsideFavorites: false
  };

  componentDidMount() {
    /*
      Handle resize so we can move the favorites/rewards link in and outside
      of the dropdown depending how close we are to the nav.
    */
    onEvent(window, 'resize', this.onResize, null, this);
    this.onResize();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.holmes !== this.props.holmes) {
      this.onResize();
    }
  }

  onResize() {
    const { isMobile } = this.props;
    // move favorites and rewards outside of dropdown if room available
    const nav = document.querySelector('[data-headernav]');
    const acct = document.getElementById('headerMyAccountDropdownToggle');

    // Added the midly excessive getBoundingClientRect bulletproofing here to avoid randomly failing tests
    if (nav && acct && nav.getBoundingClientRect && acct.getBoundingClientRect) {
      const minDistanceFavorites = 350;
      const distance = acct.getBoundingClientRect().left - nav.getBoundingClientRect().right;

      const showOutsideFavorites = distance > minDistanceFavorites && !isMobile;
      this.setState({ showOutsideFavorites });
    }
  }

  // Change url to redirect back to where we started unless we're on a login page
  makeLoginPathUrl = (pathname, search) => (!pathname.match(/^\/(ap|zap)\/?/) ? `/zap/preAuth/signin?openid.return_to=${pathname + encodeURIComponent(search)}` : '/login')

  handleSignInClick = e => {
    e.preventDefault();

    trackEvent('TE_HEADER_ACCOUNT_SIGNIN');
    trackLegacyEvent('Main-Nav', 'SignIn', 'LoginRegister');
    track(evTopLevelNavigationClick, {
      valueClicked: 'Sign In'
    });

    // Use `window` first because remote HF doesn't have its router synced up when remote.
    const { pathname, search } = window.location;
    const link = this.makeLoginPathUrl(pathname, search);
    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      window.open(link, '_blank');
    } else {
      window.location = link;
    }
  }

  render() {
    const { testId } = this.context;
    const { showOutsideFavorites } = this.state;
    const { holmes, isOpen, isVIPDashboard, handleTopNavClick, handleTopNavCloseClick,
      isMobile, location: { pathname, search }, hydraLasd,
      rewards: { rewardsInfo } } = this.props;
    const { canEnroll, consented, enrolled, prime, spendPoints, spendPointsDollarValue } = rewardsInfo || {};
    const isRewardsMember = enrolled && consented;
    let dashBoardLinkText = '';

    if (isVIPDashboard) {
      dashBoardLinkText = isRewardsMember ? 'VIP Dashboard' : canEnroll ? 'Sign up for Zappos VIP' : false;
    } else {
      dashBoardLinkText = 'My Rewards';
    }

    if (holmes) {
      const { firstName } = holmes;
      return (
        <div className={css.accountInfo} data-test-id={testId('headerAccountDropdownContainer')}>
          {showOutsideFavorites && <Link
            data-test-id={testId('headerFavorites')}
            onClick={() => {
              trackEvent('TE_HEADER_ACCOUNT_FAVORITES');
              trackLegacyEvent('Main-Nav', 'Account', 'Favorites');
              track(evTopLevelNavigationClick, {
                valueClicked: 'Favorites'
              });
            }}
            to="/account/favorites">Favorites</Link>}

          <Link
            id="headerMyAccountDropdownToggle"
            to="/account"
            data-test-id={testId('headerAccountToggle')}
            data-shyguy="headerMyAccountDropdownToggle"
            onClick={e => {
              handleTopNavClick(e);
              trackEvent('TE_HEADER_ACCOUNT_DROPDOWN');
              track(evTopLevelNavigationClick, {
                valueClicked: 'My Account'
              });
            }}
            aria-expanded={isOpen('headerMyAccountDropdownToggle')}>
            {isMobile ? 'Account' : 'My Account'}
          </Link>
          <div className={cn(css.dropdownContent, { [css.prime]: rewardsInfo && isRewardsMember && prime })} data-hfdropdown data-test-id={testId('headerMyAccountDropdown')}>
            <button
              type="button"
              data-test-id={testId('headerAccountClose')}
              data-close
              className={css.close}
              aria-label="Close My Account menu"
              onClick={handleTopNavCloseClick}/>
            <Link
              data-test-id={testId('headerWelcomeAccount')}
              className={css.heading}
              onClick={() => {
                trackEvent('TE_HEADER_ACCOUNT_WELCOME');
                track(evNavigationClick, {
                  valueClicked: 'Account Overview',
                  parentDropdown: 'My Account'
                });
              }}
              to="/account">
              {/* Sometimes the holmes cookie is goofy and thinks your name is Zappos */}
              {firstName ?
                `Welcome back, ${decodeURIComponent(firstName)}!`
                : 'Welcome back!'
              }
            </Link>

            {
              !isVIPDashboard && rewardsInfo && isRewardsMember && (
                <>
                  <p className={css.rewardsMember}>Zappos Rewards Member { prime && (<>+ <span className={css.primeBadge} aria-label="Amazon Prime badge" /> </>) }</p>
                  <h3 className={css.rewardsAmount}>{toThousandsSeparator(spendPoints)} points <span>({toUSD(spendPointsDollarValue)})</span></h3>
                  <Link to="/rewards/dashboard">Redeem My Points</Link>
                </>
              )
            }

            {
              isVIPDashboard && rewardsInfo && consented && (
                <p className={css.vipDashboard}>
                  <VipOnlyLogo />
                  { prime && <PrimeLogo fillColor="#01579b" /> }
                </p>
              )
            }

            {
              !!dashBoardLinkText && <Link
                data-test-id={testId('headerRewards')}
                onClick={() => {
                  trackEvent('TE_HEADER_ACCOUNT_REWARDS');
                  trackLegacyEvent('Main-Nav', 'Account', 'Rewards');
                  track(evNavigationClick, {
                    valueClicked: 'Rewards',
                    parentDropdown: 'My Account'
                  });
                }}
                to="/rewards/dashboard">{dashBoardLinkText}</Link>
            }

            {
              isVIPDashboard && !rewardsInfo && <Link
                data-test-id={testId('headerRewards')}
                onClick={() => {
                  trackEvent('TE_HEADER_ACCOUNT_REWARDS');
                  trackLegacyEvent('Main-Nav', 'Account', 'Rewards');
                  track(evNavigationClick, {
                    valueClicked: 'Rewards',
                    parentDropdown: 'My Account'
                  });
                }}
                to="/rewards/dashboard">VIP Dashboard</Link>
            }

            <Link
              data-test-id={testId('headerAccountOverview')}
              onClick={() => {
                trackEvent('TE_HEADER_ACCOUNT_MYACCOUNT');
                trackLegacyEvent('Main-Nav', 'Account', 'MyAccount');
                track(evNavigationClick, {
                  valueClicked: 'Account Overview',
                  parentDropdown: 'My Account'
                });
              }}
              to="/account">Account Overview</Link>
            {!showOutsideFavorites && <Link
              data-test-id={testId('headerFavorites')}
              onClick={() => {
                trackEvent('TE_HEADER_ACCOUNT_FAVORITES');
                trackLegacyEvent('Main-Nav', 'Account', 'Favorites');
                track(evNavigationClick, {
                  valueClicked: 'Favorites',
                  parentDropdown: 'My Account'
                });
              }}
              to="/account/favorites">Favorites</Link>}
            {hydraLasd && <Link
              data-test-id={testId('headerSalesForYou')}
              onClick={() => {
                trackEvent('TE_HEADER_DEALS_FOR_YOU');
                trackLegacyEvent('Main-Nav', 'Account', 'Deals for you');
                track(evNavigationClick, {
                  valueClicked: 'Deals for you',
                  parentDropdown: 'My Account'
                });
              }}
              to="/deals-for-you">Deals for you</Link>}
            <Link
              data-test-id={testId('headerSignOut')}
              onClick={() => {
                removeFromSessionStorage(SESSIONSTORAGE_KEY_REWARDS_INFO);
                trackEvent('TE_HEADER_ACCOUNT_SIGNOUT');
                trackLegacyEvent('Main-Nav', 'Account', 'SignOut');
                track(evNavigationClick, {
                  valueClicked: 'Sign Out',
                  parentDropdown: 'My Account'
                });
              }}
              to="/logout">Sign Out</Link>
          </div>
        </div>
      );
    }

    return (
      <div className={css.accountInfo}>
        <Link
          to={this.makeLoginPathUrl(pathname, search)}
          data-test-id={testId('headerSignIn')}
          onClick={this.handleSignInClick}
          className={css.heading}>
          {isMobile ? 'Sign In' : 'Sign In / Register'}
        </Link>
      </div>
    );
  }
}

AccountDropdown.contextTypes = {
  testId: PropTypes.func
};

export default AccountDropdown;
